import React, { useEffect, useState } from "react";
import { Table, message, Spin, Button, Popconfirm, Space, Tag, Select } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import { CustomListItemService } from "../../Services/CustomListItemService";
import { CustomListService } from "../../Services/CustomListService";
import { ViewService } from "../../Services/ViewService";
import { useAuth } from "../../Contexts/AuthContext";
import LayoutLeftMenu from "../../Components/Layout/LayoutLeftMenu";
import LeftMenu from "../../PagesAdmin/Users/LeftMenu";
import { CheckOutlined, CloseOutlined, FileExcelOutlined, UserOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from 'dayjs';
import { WebApiCustomListsDtoConnectedMemberDto, WebApiCustomListsDtoCustomListDto, WebApiCustomListsDtoCustomListItemDto, WebApiModelsCustomListField, WebApiServicesDataTransferObjectsGroupServiceGroupResultDto, WebApiModelsView } from "../../Repository/eventbookyapi";
import { CustomListFieldService } from "../../Services/CustomLifFieldService";
import { UiControlType } from '../../Types/UiControlType';
import { sortByUiControlType, sortNumber } from '../../Helpers/TableSortHelper';
import UploadCustomListItemButton from "../../Components/Users/UploadCustomListItems/UploadCustomListItemButton";
import CustomListAddModal from "./CustomListAddModal";
import CustomListEditModal from "./CustomListEditModal";
import ManageConnectedMembersModal from "./ManageConnectedMembersModal";
import { MemberService } from "../../Services/MemberService";
import { TextService } from "../../Services/TextService";
import { MemberFieldService } from "../../Services/MemberFieldService";
import { LookupRelationType } from "../../Types/LookupRelationType";
import { getCurrentLanguage } from "../../Helpers/RouteHelper";
import { MemberStatusType } from "../../Types/MemberStatusType";
import { CustomerPreferencesHelper } from "../../Helpers/CustomerPreferencesHelper";
import { CustomListTable } from "../../Components/Table/CustomListTable";
import { CustomListType } from "../../Types/CustomListType";
import { CustomListFieldTypes } from "../../Types/CustomListFieldTypes";

const CustomList: React.FC = () => {
    const { id, editId } = useParams<{ id: string, editId?: string }>();
    const navigate = useNavigate();
    const GetText = TextService.GetText;
    const { user } = useAuth();
    const [items, setItems] = useState<WebApiCustomListsDtoCustomListItemDto[]>([]);
    const [customList, setCustomList] = useState<WebApiCustomListsDtoCustomListDto>({});
    const [customFields, setCustomFields] = useState<WebApiModelsCustomListField[]>([]);
    const [loading, setLoading] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editItem, setEditItem] = useState<WebApiCustomListsDtoCustomListItemDto | null>(null);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [lookupOptions, setLookupOptions] = useState<Record<string, any[]>>({});
    const [memberResult, setMemberResult] = useState({} as WebApiServicesDataTransferObjectsGroupServiceGroupResultDto);
    const [manageMembersModalOpen, setManageMembersModalOpen] = useState(false);
    const [selectedItemForMembers, setSelectedItemForMembers] = useState<WebApiCustomListsDtoCustomListItemDto | null>(null);
    const [memberFields, setMemberFields] = useState<any[]>([]);
    const [availableViews, setAvailableViews] = useState<WebApiModelsView[]>([]);
    const [selectedView, setSelectedView] = useState<WebApiModelsView | null>(null);
    const [isLoadingViews, setIsLoadingViews] = useState(true);
    const isFamilyMemberList = customList?.listType === CustomListType.FamilyMember;

    // Extracted fetchData function for loading and refreshing
    const fetchData = async () => {
        if (!id) return;
        setLoading(true);
        try {
            const members = await MemberService.GetAllMembers();
            setMemberResult(members);
            // Get member fields for lookup relationship types
            const memberFieldsData = await MemberFieldService.GetMemberFields();
            setMemberFields(memberFieldsData);
            // Get list definition (for schema)
            const list = await CustomListService.GetCustomList(Number(id));
            setCustomList(list);
            const fields = await CustomListFieldService.GetCCustomListFields(Number(id));
            setCustomFields(fields);
            // Fetch lookup options for all lookup fields
            const lookupFields = fields.filter((f: WebApiModelsCustomListField) => f.uiControl === UiControlType.Lookup);
            const lookupOptionsObj: Record<string, any[]> = {};
            await Promise.all(lookupFields.map(async (field: WebApiModelsCustomListField) => {
                try {
                    const items = await CustomListItemService.GetItems(Number(field.lookupListId));
                    lookupOptionsObj[field.id!] = (items || []).map((item: WebApiCustomListsDtoCustomListItemDto) => {
                        let label = item.id?.toString();
                        if (Array.isArray(item.fieldValues) && item.fieldValues.length > 0) {
                            // Use the first FieldValue as label
                            label = item.fieldValues[0].fieldValue!
                        }
                        return { value: item.id, label };
                    });
                } catch { }
            }));
            setLookupOptions(lookupOptionsObj);

            // Fetch and set items for the table
            const itemsRaw = await CustomListItemService.GetItems(Number(id));
            const items = (itemsRaw || []).map((item: WebApiCustomListsDtoCustomListItemDto) => {
                // Map fieldValues array to flat object: { [fieldId as string]: fieldValue }
                let fieldValuesObj: Record<string, any> = {};
                const fvArr = item.fieldValues || item.fieldValues;
                if (Array.isArray(fvArr)) {
                    fvArr.forEach((fv: any) => {
                        fieldValuesObj[fv.fieldId != null ? fv.fieldId.toString() : fv.FieldId.toString()] = fv.fieldValue ?? fv.FieldValue;
                    });
                }
                return { ...item, ...fieldValuesObj };
            });

            setItems(items);
        } catch (e) {
            message.error(GetText("CustomList_FailedToLoad") || "Could not load list data.");
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [id]);

    // Load views for this custom list
    useEffect(() => {
        const loadViews = async () => {
            if (!id) {
                setIsLoadingViews(false);
                return;
            }

            try {
                const views = await ViewService.GetAllViews();
                // Filter to Custom List View type (viewType === 1) and matching customListId
                const customListViews = views.filter(view =>
                    view.viewType === 1 && view.customListId === Number(id)
                );
                setAvailableViews(customListViews);

                // Restore saved view preference for this list
                const savedViewId = CustomerPreferencesHelper.getSelectedView();
                if (savedViewId && savedViewId !== 'default') {
                    const savedView = customListViews.find(v => v.id === savedViewId);
                    if (savedView) {
                        setSelectedView(savedView);
                    } else {
                        CustomerPreferencesHelper.setSelectedView('default');
                    }
                }
            } catch (error) {
                console.error('Failed to load views:', error);
            } finally {
                setIsLoadingViews(false);
            }
        };

        loadViews();
    }, [id]);

    // Open edit or add modal if editId is present in route
    useEffect(() => {
        if (editId === "add" && customFields.length > 0) {
            handleAdd();
        } else if (editId && editId !== "add" && items.length > 0) {
            const item = items.find(i => String(i.id) === String(editId));
            if (item) {
                handleEdit(item);
            }
        }
        // eslint-disable-next-line
    }, [editId, items, customFields]);

    // Build columns from customFields
    const columns = [
        // Connected Members Column - First
        {
            title: <><UserOutlined /> {GetText("CustomList_ConnectedMembers") || "Connected Members"}</>,
            key: "connectedMembers",
            sorter: (a: any, b: any) => sortNumber((a.connectedMembers || []).length, (b.connectedMembers || []).length),
            render: (_: any, record: WebApiCustomListsDtoCustomListItemDto) => {
                const connectedMembers = record.connectedMembers || [];
                const hasMembers = connectedMembers.length > 0;
                return (
                    <Space wrap align="center">
                        {hasMembers ? (
                            connectedMembers.map((member: WebApiCustomListsDtoConnectedMemberDto, index: number) => {
                                const isCancelled = member.status === MemberStatusType.Cancelled;
                                const memberUrl = `/${getCurrentLanguage()}/users/${member.memberId}`;
                                return (
                                    <a
                                        key={index}
                                        href={memberUrl}
                                        onClick={(e) => {
                                            // Allow ctrl/cmd+click and middle-click to open in new tab
                                            if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                                e.preventDefault();
                                                navigate(memberUrl);
                                            }
                                        }}
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <Tag
                                            color={isCancelled ? "red" : "blue"}
                                            icon={<UserOutlined />}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {member.firstName} {member.lastName}
                                            {isCancelled && ` (${GetText("MemberList_StatusCancelled")})`}
                                        </Tag>
                                    </a>
                                );
                            })
                        ) : (
                            <span style={{ color: '#999' }}>-</span>
                        )}
                        {hasMembers ? (
                            !isFamilyMemberList && !user?.hasOnlyReadRights && (
                                <EditOutlined
                                    style={{ fontSize: '20px', color: '#1890ff', cursor: 'pointer' }}
                                    onClick={() => handleManageMembers(record)}
                                />
                            )
                        ) : (
                            !isFamilyMemberList && !user?.hasOnlyReadRights && (
                                <PlusOutlined
                                    style={{ fontSize: '20px', color: '#1890ff', cursor: 'pointer' }}
                                    onClick={() => handleManageMembers(record)}
                                />
                            )
                        )}
                    </Space>
                );
            },
        },
        ...customFields.map((field: any) => {
            let col: any = {
                title: field.nameAdmin,
                dataIndex: String(field.id),
                key: String(field.id),
            };

            console.log(field)

            if (field.customListFieldTypes == CustomListFieldTypes.EmailNotification) {
                col.render = (value: any) =>
                    value === true ? <CheckOutlined style={{ color: 'green' }} /> : value === false ? null : null;
            }

            // Add sorting based on field type
            if (field.uiControl === UiControlType.YesNo && field.customListFieldTypes !== CustomListFieldTypes.EmailNotification) {
                col.render = (value: any) =>
                    value === true ? <CheckOutlined style={{ color: 'green' }} /> : value === false ? <CloseOutlined style={{ color: 'red' }} /> : null;
            }
            if (field.uiControl === UiControlType.Date) {
                col.render = (value: any) => {
                    if (!value) return '';
                    if (dayjs.isDayjs(value)) {
                        return value.isValid() ? value.format('YYYY-MM-DD') : '';
                    }
                    const d = dayjs(value);
                    return d.isValid() ? d.format('YYYY-MM-DD') : '';
                };
            }
            if (field.uiControl === UiControlType.Lookup) {
                col.render = (value: any) => {
                    const opts = lookupOptions[field.id!] || [];
                    // Try to match both number and string types
                    const found = opts.find((opt: any) => String(opt.value) === String(value));
                    return found ? found.label : (value ?? "");
                };
            }
            if (field.uiControl === UiControlType.Number) {
                col.align = 'right';
                col.render = (value: any) => value != null ? Number(value).toLocaleString(getCurrentLanguage() === 'se' ? 'sv-SE' : 'en-US') : '';
            }
            if (field.uiControl === UiControlType.Currency) {
                col.align = 'right';
                col.render = (value: any) => value != null ? `${Number(value).toLocaleString(getCurrentLanguage() === 'se' ? 'sv-SE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${GetText("Common_CurrencySymbol")}` : '';
            }
            if (field.uiControl === UiControlType.Decimal) {
                col.align = 'right';
                col.render = (value: any) => value != null ? Number(value).toLocaleString(getCurrentLanguage() === 'se' ? 'sv-SE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
            }
            if (field.uiControl === UiControlType.Email) {
                col.render = (value: any) => value ? <a href={`mailto:${value}`}>{value}</a> : '';
            }
            if (field.uiControl === UiControlType.PhoneNumber) {
                col.render = (value: any) => value ? <a href={`tel:${value}`}>{value}</a> : '';
            }

            // Unified sorter using centralized helper
            col.sorter = (a: any, b: any) => {
                const opts = field.uiControl === UiControlType.Lookup ? (lookupOptions[field.id!] || []) : undefined;
                return sortByUiControlType(field.uiControl, a[field.id], b[field.id], opts);
            };

            return col;
        }),
        {
            title: GetText("MemberList_ActionColumn") || "Actions",
            key: "actions",
            render: (_: any, record: any) => (
                <Space>
                    <Button size="small" onClick={() => handleEdit(record)}>
                        {GetText("Common_Edit") || "Edit"}
                    </Button>
                    <Popconfirm
                        title={GetText("Common_ConfirmDelete") || "Are you sure you want to delete?"}
                        onConfirm={() => handleDelete(record)}
                        okText={GetText("Common_Yes") || "Yes"}
                        cancelText={GetText("Common_No") || "No"}
                    >
                        {!user?.hasOnlyReadRights && (
                            <Button size="small" danger>
                                {GetText("Common_Delete") || "Delete"}
                            </Button>
                        )}
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    function handleEditCancel() {
        setEditModalOpen(false);
    }

    function handleManageMembers(item: WebApiCustomListsDtoCustomListItemDto) {
        if (isFamilyMemberList) {
            return;
        }

        setSelectedItemForMembers(item);
        setManageMembersModalOpen(true);
    }

    async function handleDelete(item: any) {
        setLoading(true);
        try {
            await CustomListItemService.DeleteCustomListItem(item.id);
            setItems(items.filter((i: any) => i.id !== item.id));
            message.success(GetText("CustomList_ItemDeleted") || "Deleted!");
        } catch {
            message.error(GetText("CustomList_FailedToDelete") || "Could not delete the item.");
        }
        setLoading(false);
    }

    // Handler to open edit modal from anywhere
    function handleEdit(item: any) {
        setEditItem(item);
        setEditModalOpen(true);
    }

    // Handler to open add modal from anywhere
    function handleAdd() {
        if (isFamilyMemberList) {
            return;
        }

        setAddModalOpen(true);
    }

    const downloadExcel = async () => {
        const data = await CustomListService.GenerateExcel(id ? Number(id) : 0);
        const url = window.URL.createObjectURL(await data.raw.blob());
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Members_${Date.now()}.xlsx`);
        document.body.appendChild(link);
        link.click();
    }

    return (
        <LayoutLeftMenu menu={<LeftMenu selected={"customList/" + id} />}>
            <h2>{customList?.name || GetText("CustomList_List") || "List"}</h2>

            <div style={{ marginBottom: 16 }}>
                {!isFamilyMemberList && (
                    <Button type="primary" onClick={handleAdd} disabled={user?.hasOnlyReadRights}>
                        {GetText("Common_Add") || "Add"}
                    </Button>
                )}
                <Button type="default" icon={<FileExcelOutlined />} style={{ marginLeft: 8 }} onClick={downloadExcel}>
                    {GetText("CustomList_ExportToExcel") || "Export to Excel"}
                </Button>

                {!user?.hasOnlyReadRights ? (<UploadCustomListItemButton
                    listId={Number(id!)}
                    members={memberResult.members || []}
                    fieldSettings={customFields}
                    onUploadSuccess={() => {
                        message.success(GetText("CustomList_UploadSuccess") || "Upload successful!");
                        fetchData(); // Refresh data after upload
                    }}
                />) : null}

                <div style={{ float: 'right' }}>
                    {!isLoadingViews && availableViews.length > 0 && (
                        <Select
                            style={{ width: 200 }}
                            placeholder={GetText("MemberList_SelectView") || "Select View"}
                            value={selectedView?.id || 'default'}
                            onChange={(value) => {
                                const viewValue = value === 'default' ? 'default' : Number(value);
                                CustomerPreferencesHelper.setSelectedView(viewValue);

                                if (value === 'default') {
                                    setSelectedView(null);
                                } else {
                                    const view = availableViews.find(v => v.id === value);
                                    setSelectedView(view || null);
                                }
                            }}
                            options={[
                                { value: 'default', label: GetText('MemberList_StandardView') || 'Standard View' },
                                ...availableViews.map(view => ({ value: view.id, label: view.name }))
                            ]}
                        />
                    )}
                </div>
            </div>

            {loading ? <Spin /> : (
                <>
                    {!isLoadingViews && selectedView ? (
                        <CustomListTable
                            view={selectedView}
                            items={items}
                            customFields={customFields}
                            lookupOptions={lookupOptions}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onManageMembers={handleManageMembers}
                            disableManageMembers={isFamilyMemberList}
                            hasOnlyReadRights={user?.hasOnlyReadRights}
                        />
                    ) : (
                        <Table
                            columns={columns}
                            dataSource={items}
                            rowKey={item => item.id!}
                            scroll={{ x: 'max-content' }}
                            pagination={false}
                        />
                    )}
                    <CustomListEditModal
                        open={editModalOpen}
                        onClose={() => setEditModalOpen(false)}
                        onSuccess={() => {
                            setEditModalOpen(false);
                            fetchData();
                        }}
                        customFields={customFields}
                        item={editItem!}
                        hasOnlyReadRights={user?.hasOnlyReadRights}
                    />
                    <CustomListAddModal
                        open={addModalOpen}
                        onClose={() => setAddModalOpen(false)}
                        onSuccess={() => {
                            setAddModalOpen(false);
                            fetchData();
                        }}
                        customFields={customFields}
                        customListId={Number(id)}
                        hasOnlyReadRights={user?.hasOnlyReadRights}
                    />
                    {selectedItemForMembers && (
                        <ManageConnectedMembersModal
                            open={manageMembersModalOpen}
                            onClose={() => {
                                setManageMembersModalOpen(false);
                                setSelectedItemForMembers(null);
                            }}
                            onSuccess={() => {
                                fetchData();
                            }}
                            customListItemId={selectedItemForMembers.id!}
                            customListId={Number(id)}
                            connectedMembers={selectedItemForMembers.connectedMembers || []}
                            allMembers={memberResult.members || []}
                            lookupRelationType={memberFields.find(f => f.customListId === Number(id))?.lookupRelationType ?? LookupRelationType.None}
                        />
                    )}
                </>
            )}
        </LayoutLeftMenu>
    );
};

export default CustomList;